import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Radio, Space, Spin, Tag, Upload, message } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, CheckOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { productsApi } from '../../utils/api';

/**
 * Éditeur léger de visuel pour les annonces Meta Ads.
 *
 * Il permet soit d'importer une image telle quelle, soit d'assembler manuellement
 * de deux à quatre images locales en une seule image publicitaire. Aucun appel IA
 * n'est effectué : le montage est créé dans le navigateur avec un canvas HTML.
 */

const MISES_EN_PAGE = [
  { value: 'horizontal', label: 'Côte à côte' },
  { value: 'vertical', label: 'Haut / bas' },
  { value: 'grid', label: 'Grille 2 × 2' }
];

/**
 * Convertit un fichier importé en data URL exploitable par l'aperçu et le canvas.
 * @param {File} fichier - Image locale choisie par l'utilisateur
 * @returns {Promise<string>} Image encodée en base64
 */
const lireImage = (fichier) => new Promise((resolve, reject) => {
  const lecteur = new FileReader();
  lecteur.onload = () => resolve(lecteur.result);
  lecteur.onerror = () => reject(new Error('Lecture de l’image impossible.'));
  lecteur.readAsDataURL(fichier);
});

/**
 * Charge une data URL dans un élément Image avant son dessin dans le canvas.
 * @param {string} source - Image encodée en data URL
 * @returns {Promise<HTMLImageElement>} Image prête à être dessinée
 */
const chargerImageCanvas = (source) => new Promise((resolve, reject) => {
  const image = new window.Image();
  // Les images de la bibliothèque sont publiques : cette propriété autorise leur
  // dessin dans un canvas exportable, sans contaminer le canvas du montage.
  image.crossOrigin = 'anonymous';
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('Chargement de l’image impossible.'));
  image.src = source;
});

/**
 * Dessine une image au centre d'une zone sans la déformer, avec recadrage si nécessaire.
 * @param {CanvasRenderingContext2D} contexte - Contexte de dessin du canvas
 * @param {HTMLImageElement} image - Image à placer
 * @param {number} x - Position horizontale de la zone
 * @param {number} y - Position verticale de la zone
 * @param {number} largeur - Largeur de la zone
 * @param {number} hauteur - Hauteur de la zone
 */
const dessinerImageCouverte = (contexte, image, x, y, largeur, hauteur) => {
  const ratio = Math.max(largeur / image.width, hauteur / image.height);
  const largeurDessinee = image.width * ratio;
  const hauteurDessinee = image.height * ratio;
  contexte.drawImage(image, x + (largeur - largeurDessinee) / 2, y + (hauteur - hauteurDessinee) / 2, largeurDessinee, hauteurDessinee);
};

/**
 * Retourne les zones de placement d'une mise en page pour le nombre d'images retenu.
 * @param {string} miseEnPage - Disposition demandée
 * @param {number} nombre - Nombre d'images à disposer
 * @param {number} largeur - Largeur du canvas final
 * @param {number} hauteur - Hauteur du canvas final
 * @returns {Array<{x: number, y: number, largeur: number, hauteur: number}>} Zones de dessin
 */
const calculerZones = (miseEnPage, nombre, largeur, hauteur) => {
  if (miseEnPage === 'vertical') {
    return Array.from({ length: nombre }, (_, index) => ({ x: 0, y: index * hauteur / nombre, largeur, hauteur: hauteur / nombre }));
  }
  if (miseEnPage === 'grid') {
    return Array.from({ length: nombre }, (_, index) => ({
      x: (index % 2) * largeur / 2,
      y: Math.floor(index / 2) * hauteur / 2,
      largeur: largeur / 2,
      hauteur: hauteur / 2
    }));
  }
  return Array.from({ length: nombre }, (_, index) => ({ x: index * largeur / nombre, y: 0, largeur: largeur / nombre, hauteur }));
};

/**
 * @param {object} props
 * @param {string} [props.productId] - Produit auquel sauvegarder le nouveau visuel
 * @param {string[]} [props.imagesInitiales] - Visuels générés à préparer dans le montage
 * @param {Function} props.onSelect - Reçoit l'URL publique du visuel prêt à utiliser
 * @param {Function} props.onClose - Ferme la fenêtre de choix de visuel après sauvegarde
 */
const EditeurCompositionAnnonce = ({ productId, imagesInitiales = [], onSelect, onClose }) => {
  const [images, setImages] = useState([]);
  const [miseEnPage, setMiseEnPage] = useState('horizontal');
  const [enregistrement, setEnregistrement] = useState(false);
  const [imagesBibliotheque, setImagesBibliotheque] = useState([]);
  const [chargementBibliotheque, setChargementBibliotheque] = useState(false);

  // Reçoit les visuels générés sélectionnés dans l'onglet voisin et les place
  // immédiatement dans l'éditeur. La limite de quatre garde le montage lisible.
  useEffect(() => {
    if (imagesInitiales.length === 0) return;
    setImages(imagesInitiales.slice(0, 4).map((source, index) => ({ nom: `Visuel généré ${index + 1}`, source })));
  }, [imagesInitiales]);

  /** Charge les images déjà associées au produit, y compris les visuels générés précédemment. */
  const chargerBibliotheque = useCallback(async () => {
    if (!productId) return;
    try {
      setChargementBibliotheque(true);
      const reponse = await productsApi.getById(productId);
      const produit = reponse?.data;
      if (!produit) return;
      const candidates = [
        ...(produit.generatedImages || []).map(image => ({ url: image.image_url, origine: 'générée' })),
        ...(produit.images || []).map(image => ({ url: image.image_url, origine: 'produit' }))
      ];
      const urlsVues = new Set();
      setImagesBibliotheque(candidates.filter(image => image.url && !urlsVues.has(image.url) && urlsVues.add(image.url)));
    } catch (error) {
      console.error('Erreur lors du chargement de la bibliothèque de composition:', error);
      message.error('Impossible de charger les images du produit.');
    } finally {
      setChargementBibliotheque(false);
    }
  }, [productId]);

  // La bibliothèque est relue à l'ouverture de l'éditeur pour inclure les derniers
  // visuels enregistrés depuis les autres onglets de la fenêtre.
  useEffect(() => {
    chargerBibliotheque();
  }, [chargerBibliotheque]);

  /** Ajoute ou retire une image de la bibliothèque du montage en préparation. */
  const basculerImageBibliotheque = (image) => {
    if (images.some(element => element.source === image.url)) {
      setImages(precedentes => precedentes.filter(element => element.source !== image.url));
      return;
    }
    if (images.length >= 4) {
      message.warning('Le montage accepte au maximum quatre images.');
      return;
    }
    setImages(precedentes => [...precedentes, { nom: `Image ${image.origine}`, source: image.url }]);
  };

  /** Ajoute une ou plusieurs images locales, avec une limite de quatre pour un montage lisible. */
  const importerImages = async (fichiers) => {
    const listeFichiers = fichiers instanceof File ? [fichiers] : Array.from(fichiers);
    const acceptes = listeFichiers.filter(fichier => fichier.type.startsWith('image/') && fichier.size <= 5 * 1024 * 1024).slice(0, 4 - images.length);
    if (acceptes.length === 0) {
      message.warning(images.length >= 4 ? 'Le montage accepte au maximum quatre images.' : 'Choisissez des images de 5 Mo maximum.');
      return false;
    }
    try {
      setImages(precedentes => [...precedentes, ...acceptes.map(fichier => ({ nom: fichier.name, source: null }))]);
      const sources = await Promise.all(acceptes.map(lireImage));
      setImages(precedentes => {
        const sansAttente = precedentes.filter(image => image.source);
        return [...sansAttente, ...sources.map((source, index) => ({ nom: acceptes[index].name, source }))];
      });
    } catch (error) {
      message.error(error.message);
    }
    return false;
  };

  /** Déplace une image dans la séquence qui détermine sa position dans le montage. */
  const deplacerImage = (index, direction) => {
    const destination = index + direction;
    if (destination < 0 || destination >= images.length) return;
    const suivantes = [...images];
    [suivantes[index], suivantes[destination]] = [suivantes[destination], suivantes[index]];
    setImages(suivantes);
  };

  /** Supprime une image locale du montage en préparation. */
  const supprimerImage = (index) => setImages(precedentes => precedentes.filter((_, position) => position !== index));

  /** Construit le montage dans un canvas puis l'enregistre dans la bibliothèque du produit. */
  const enregistrerMontage = async () => {
    const imagesPretes = images.filter(image => image.source);
    if (imagesPretes.length === 0) {
      message.warning('Importez au moins une image.');
      return;
    }
    try {
      setEnregistrement(true);
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1350;
      const contexte = canvas.getContext('2d');
      contexte.fillStyle = '#ffffff';
      contexte.fillRect(0, 0, canvas.width, canvas.height);
      const imagesChargees = await Promise.all(imagesPretes.map(image => chargerImageCanvas(image.source)));
      const zones = calculerZones(miseEnPage, imagesChargees.length, canvas.width, canvas.height);
      imagesChargees.forEach((image, index) => dessinerImageCouverte(contexte, image, zones[index].x, zones[index].y, zones[index].largeur, zones[index].hauteur));
      const { data } = await productsApi.saveGeneratedImage({
        prompt: `Montage manuel ${miseEnPage} pour publicité Meta Ads`,
        imageUrl: canvas.toDataURL('image/jpeg', 0.92),
        model: 'manual-composition',
        productId: productId || null
      });
      const url = data?.image_url || data?.data?.image_url;
      if (!url) throw new Error('Aucune URL publique n’a été renvoyée.');
      onSelect(url);
      message.success('Visuel enregistré et appliqué à l’annonce.');
      onClose();
    } catch (error) {
      console.error('Erreur lors de la création du montage:', error);
      message.error(error.response?.data?.message || 'Impossible d’enregistrer le visuel.');
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <div className="space-y-4">
      <Alert type="info" showIcon message="Importez une image seule ou composez jusqu’à quatre images, sans génération IA." />
      {productId && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Images déjà présentes dans le produit</span>
            {chargementBibliotheque && <Spin size="small" />}
          </div>
          {imagesBibliotheque.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {imagesBibliotheque.map(image => {
                const selectionnee = images.some(element => element.source === image.url);
                return (
                  <button
                    key={image.url}
                    type="button"
                    onClick={() => basculerImageBibliotheque(image)}
                    className={`relative overflow-hidden rounded border-2 ${selectionnee ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    <img src={image.url} alt="Visuel du produit" className="w-full aspect-square object-cover" />
                    <Tag color={image.origine === 'générée' ? 'purple' : 'default'} className="absolute left-1 top-1 m-0 text-[10px]">{image.origine}</Tag>
                    {selectionnee && <span className="absolute inset-x-0 bottom-0 bg-blue-600 py-0.5 text-[10px] text-white">sélectionnée</span>}
                  </button>
                );
              })}
            </div>
          ) : !chargementBibliotheque && <p className="text-xs text-gray-500">Aucune image enregistrée pour ce produit.</p>}
        </div>
      )}
      <Upload multiple accept="image/*" showUploadList={false} beforeUpload={importerImages}>
        <Button icon={<UploadOutlined />}>Importer des images</Button>
      </Upload>
      {images.length > 0 && (
        <>
          <Radio.Group value={miseEnPage} onChange={event => setMiseEnPage(event.target.value)} options={MISES_EN_PAGE} optionType="button" buttonStyle="solid" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {images.map((image, index) => (
              <Card key={`${image.nom}-${index}`} size="small" cover={image.source ? <img src={image.source} alt={image.nom} className="aspect-square object-cover" /> : <div className="aspect-square bg-gray-100" />}>
                <Space size="small">
                  <Button size="small" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => deplacerImage(index, -1)} />
                  <Button size="small" icon={<ArrowDownOutlined />} disabled={index === images.length - 1} onClick={() => deplacerImage(index, 1)} />
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => supprimerImage(index)} />
                </Space>
              </Card>
            ))}
          </div>
          <Button type="primary" icon={<CheckOutlined />} loading={enregistrement} onClick={enregistrerMontage}>
            {images.length === 1 ? 'Enregistrer cette image' : 'Créer et utiliser le montage'}
          </Button>
        </>
      )}
    </div>
  );
};

export default EditeurCompositionAnnonce;
